// External dependencies
import { useRef } from 'react';
import classNames from 'classnames';
import { RxDragHandleDots2 } from 'react-icons/rx';
import { MdLockOpen } from 'react-icons/md';

// Internal types
import { EventAnalyticsTicketScansByTierDataPointDto } from 'legacy/events/types';
import { OutgoingEventProductDto } from 'api/dtos/events/products';

// Utils
import { currencyFormat } from 'legacy/common/utils/currency';

// Components
import PrivateTicketInfo from './components/PrivateTicketInfo';
import TicketStats from './components/TicketStats';
import TicketToggles from './components/TicketToggles';

// Hooks
import { useShareUrl } from './hooks/useShareUrl';

type TicketProps = {
  ticket: OutgoingEventProductDto;
  testId?: string;
  dataTestId?: string;
  eventId?: string;
  eventShareToken?: string;
  onClick: () => void;
  toggleTicketTier: (isDisabled: boolean) => void;
  isSortable?: boolean;
  toggleHideTier: () => void;
  tierScansData?: EventAnalyticsTicketScansByTierDataPointDto;
};

const Ticket: React.FC<TicketProps> = ({
  eventId,
  eventShareToken,
  ticket,
  testId,
  onClick,
  toggleTicketTier,
  isSortable,
  toggleHideTier,
  tierScansData
}) => {
  const ticketRef = useRef<HTMLDivElement>(null);
  const shareUrl = useShareUrl(eventId, eventShareToken, ticket.shareToken);

  return (
    <div onClick={onClick} data-testid={testId} ref={ticketRef}>
      <div
        className={classNames('relative w-full max-w-[550px]', {
          'opacity-60': !ticket.isActive
        })}
      >
        <div
          className={classNames(
            'w-full flex justify-between relative cursor-pointer border-1 rounded-2xl p-4 border-gray-200 hover:border-gray-500'
          )}
        >
          {isSortable && (
            <div className="absolute top-5 right-4">
              <RxDragHandleDots2
                size={25}
                className="cursor-grab text-[#787878]"
              />
            </div>
          )}

          <div className="w-full">
            <div className="flex items-center w-[85%]">
              <div>
                <div className="flex items-start font-normal text-base text-[#202020]">
                  {ticket.isPrivate && (
                    <span data-testid={`${testId}-lock-icon`}>
                      <MdLockOpen size={20} className="mr-1" />
                    </span>
                  )}
                  <span data-testid={`${testId}-name`}>{ticket.name}</span>
                </div>
                <div className="text-base font-bold text-[#202020]">
                  {currencyFormat(ticket.price)}
                </div>
              </div>
            </div>

            <PrivateTicketInfo ticket={ticket} shareUrl={shareUrl} />

            {ticket.description && (
              <div
                data-testid={`${testId}-description`}
                className="text-[#575757] text-base font-normal mt-4"
              >
                {ticket.description}
              </div>
            )}

            <div className="mt-4 w-full border-t border-[#EBEBEB]" />

            <TicketStats
              tierScansData={tierScansData}
              purchases={ticket.purchases}
              maxAttendance={ticket.maxQuantity}
              requiresApproval={ticket.purchaseRequirements?.requiresApproval}
              questions={ticket.purchaseRequirements?.questions}
              testId={testId}
            />

            <div className="mt-4 w-full border-t border-[#EBEBEB]" />

            <TicketToggles
              ticket={ticket}
              toggleTicketTier={toggleTicketTier}
              toggleHideTier={toggleHideTier}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Ticket;
